import React, { Suspense, useEffect } from 'react';
import { Toaster } from 'sonner';

import AppRoutes from './routes';
import AnalyticsBootstrap from './shared/analytics/AnalyticsBootstrap';
import HubSpotGate from './shared/analytics/HubSpotGate';
import CookieBanner from './shared/components/ui/CookieBanner';
import LoadingSkeleton from './shared/components/ui/LoadingSkeleton';
import { ThemeProvider } from './shared/contexts/ThemeContext';
import { useAuth } from './shared/hooks/useAuth';
import { QueryProvider } from './shared/providers/QueryProvider';
import { needsAuthCheck } from './shared/routes/paths';

const App: React.FC = () => {
  const { checkAuth } = useAuth();

  useEffect(() => {
    // Public pages never read the session (nothing under src/pages/public
    // touches useAuth), so probing there costs a credentialed cross-origin
    // round trip on the SEO-critical prerendered landing page and buys nothing.
    // It was also the source of the phantom `auth_login_failed` volume: the
    // probe failed for anonymous visitors who never tried to log in.
    // useAuth seeds hasCheckedAuth for this case, so analytics still reports
    // these visitors as 'guest' rather than 'unknown'.
    if (needsAuthCheck(window.location.pathname)) {
      checkAuth();
    }
  }, [checkAuth]);

  return (
    <ThemeProvider>
      <QueryProvider>
        <AnalyticsBootstrap />
        <HubSpotGate />
        <Suspense fallback={<LoadingSkeleton />}>
          <AppRoutes />
        </Suspense>
        <Toaster
          position="bottom-left"
          toastOptions={{
            style: {
              border: '1px solid var(--color-border-static)'
            },
            className: 'custom-toast'
          }}
        />
        {/* Mounted here, outside the route tree, so consent is gated on both the
            public pages and the authenticated app — the same surfaces PostHog
            runs on. */}
        <CookieBanner />
      </QueryProvider>
    </ThemeProvider>
  );
};

export default App;
